<template>
	<div class="offer__presentation" @click.prevent.stop="$emit('openBuyBoxInfoModal')">
		<div class="offer__presentation__icons">
			<p class="offer__presentation__icons__age" v-if="isDvdOrBluRay">
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
					<path
						d="M0.666016 8.00008C0.666016 3.94956 3.94883 0.666748 7.99935 0.666748C12.0499 0.666748 15.3327 3.94956 15.3327 8.00008C15.3327 12.0506 12.0499 15.3334 7.99935 15.3334C3.94883 15.3334 0.666016 12.0506 0.666016 8.00008ZM5.24935 8.00008C5.24935 9.51831 6.48112 10.7501 7.99935 10.7501C9.51758 10.7501 10.7493 9.51831 10.7493 8.00008C10.7493 6.48185 9.51758 5.25008 7.99935 5.25008C6.48112 5.25008 5.24935 6.48185 5.24935 8.00008Z"
						fill="#FBC500"
					/>
				</svg>
			</p>
			<p class="offer__presentation__icons__subtitles">CC</p>
			<p v-if="presentationTypeLabel" class="offer__presentation__icons__quality">
				{{ presentationTypeLabel }}
			</p>
			<p v-if="ageRating" class="offer__presentation__icons__age">
				<svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
					<circle cx="6" cy="6" r="5.5" stroke="#5AA152" />
					<path d="M3.5 5.5L7 8.5L14 1" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
				</svg>
				{{ ageRating }}
			</p>
			<p v-if="dolbyTechnology" class="offer__presentation__icons__dolby">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="20"
					height="16"
					fill="#fff"
					role="img"
					viewBox="0 3.65 24 16.7"
				>
					<path
						d="M24 20.352V3.648H0v16.704h24zM18.433 5.806h2.736v12.387h-2.736c-2.839 0-5.214-2.767-5.214-6.194s2.375-6.193 5.214-6.193zm-15.602 0h2.736c2.839 0 5.214 2.767 5.214 6.194s-2.374 6.194-5.214 6.194H2.831V5.806z"
					></path>
				</svg>
			</p>
		</div>

		<p class="offer__presentation__info">
			<strong v-if="showText">{{ showText }} - </strong>
			<span v-if="runtimeMins">{{ runtimeMins }}</span>
			<span v-if="audioLanguages"> - {{ audioLanguages }}</span>
		</p>
	</div>
</template>

<script lang="ts" setup>
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyboxRegularOffer } from '@/features/buybox/types/types';
import { computed } from 'vue';
import { getTranslatableForShowOfferGraphql } from '@/helpers/offer-helper';
import { AudioTechnology, PresentationType, VideoTechnology } from '@/@types/graphql-types';

const { t, te } = useI18n();
const props = defineProps<{
	offer: BuyboxRegularOffer;
	title: TitleDetail;
	seasonNumber?: number | null;
}>();

defineEmits(['openBuyBoxInfoModal']);

const runtimeMins = computed(() => (props.title?.content?.runtime ? `${props.title.content.runtime}min` : null));

const isDvdOrBluRay = computed(() =>
	[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(props.offer?.presentationType)
);

const showText = computed(() => {
	if (isDvdOrBluRay.value && props.seasonNumber != null) {
		return null;
	}
	return getTranslatableForShowOfferGraphql(props.offer, props.title.objectType);
});

const audioLanguages = computed(() => {
	if (!props.offer?.audioLanguages) return false;
	return languageArrayToString(props.offer.audioLanguages);
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season') {
		return props.title.content.ageCertification;
	}
	return null;
});

const dolbyTechnology = computed(() => {
	return (
		props.offer?.videoTechnology.includes(VideoTechnology.DolbyVision) ||
		props.offer?.audioTechnology.includes(AudioTechnology.Dolby_5Point_1) ||
		props.offer?.audioTechnology.includes(AudioTechnology.DolbyAtmos)
	);
});

const presentationTypeLabel = computed(() => {
	switch (props.offer?.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'SD';
		case PresentationType.Bluray:
			return 'HD';
		case PresentationType.Bluray_4K:
			return '4K UHD ';
		default:
			return '';
	}
});

function languageArrayToString(languageArray: string[]) {
	return languageArray
		.map(language => {
			const key = `WEBAPP_LANGUAGE_${language.toUpperCase()}`;
			if (te(key)) {
				return t(key);
			}
			return null;
		})
		.filter(Boolean)
		.join(', ');
}
</script>

<style lang="scss" scoped>
.offer__presentation {
	display: flex;
	flex-direction: column;
	width: 100%;

	@media (max-width: 1024px) {
		justify-content: flex-end;
		gap: 4px;
		margin-top: 26px;
	}

	&__icons {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-grow: 1;
		margin-bottom: 4px;
		margin-top: 1px;
		max-height: 12px;

		@media (max-width: 1024px) {
			flex-grow: 0;
			justify-content: flex-start;
		}

		&__subtitles,
		&__quality {
			color: #c6c8cd;
			font-size: 11px;
			letter-spacing: 0.18px;
			border-radius: 2px;
			border: 1px solid #c6c8cd;
			padding: 0 2px;
			margin: 0;
			line-height: 16px;
		}

		&__quality {
			color: #fbc500;
			border: 1px solid #fbc500;
		}

		&__age,
		&__dolby {
			display: flex;
			align-items: center;
			gap: 1px;
			color: #fff;
			font-size: 11px;
			line-height: 16px;
			letter-spacing: 0.18px;
			margin: 0;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}

	&__info {
		margin: 0;
		color: #c6c8cd;
		font-size: 14px;
		line-height: 24px;
		letter-spacing: 0.21px;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;

		@media (max-width: 1024px) {
			margin-top: 0;
			margin-bottom: 0;
			-webkit-line-clamp: 1;
		}
	}
}
</style>
